import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Warehouse, Store, AlertTriangle, ShoppingCart, Wheat } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import api, { platformAPI, inventoryAPI, relationshipAPI } from '@/lib/api';
import HomeShell from '@/components/home/HomeShell';
import HomeTiles from '@/components/home/HomeTiles';
import HomeLatestDeals from '@/components/home/HomeLatestDeals';
import HomeBigButtons from '@/components/home/HomeBigButtons';
import {
  HomeSearchBar, HomeSectionTitle, HomeStakeholderStrip, HomeStatsCard,
} from '@/components/home/HomeBlocks';

function _rowsOf(res) {
  if (res.status !== 'fulfilled') return [];
  const data = res.value?.data;
  if (Array.isArray(data)) return data;
  return data?.items || [];
}

// Same order on every role's home: people circles, search, the two big
// yellow buttons (the products marketplace and the farm produce market;
// beta feedback replaced the add-products / spreadsheet / add-a-shop
// buttons, which live under My stock and My shopkeepers), latest deals,
// my statistics, tiles.
export default function DistributorHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [deals, setDeals] = useState([]);
  const [platform, setPlatform] = useState({});
  const [stats, setStats] = useState({ items: 0, low: 0, shopkeepers: 0 });
  const [searchQ, setSearchQ] = useState('');

  const loadData = useCallback(async () => {
    const [invRes, dealRes, lowRes, netRes, platRes] = await Promise.allSettled([
      inventoryAPI.get(user?.id),
      api.get(`/deals/for-user/${user?.id}`, { params: { sort: 'newest', limit: 10 } }),
      inventoryAPI.getLowStock(user?.id),
      relationshipAPI.getNetwork(user?.id),
      platformAPI.getStatistics(),
    ]);
    setDeals(_rowsOf(dealRes));
    setStats({
      items: _rowsOf(invRes).length,
      low: _rowsOf(lowRes).length,
      shopkeepers: netRes.status === 'fulfilled'
        ? ((netRes.value.data || {}).downstream || []).length
        : 0,
    });
    if (platRes.status === 'fulfilled') setPlatform(platRes.value.data || {});
  }, [user?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchQ.trim();
    navigate(q ? `/inventory?search=${encodeURIComponent(q)}` : '/inventory');
  };

  const statTiles = [
    { icon: Warehouse, iconClass: 'text-farm-primary bg-farm-primary/10',
      value: stats.items, label: t('distributorHome.stats.inventoryItems') },
    { icon: AlertTriangle, iconClass: 'text-farm-warning bg-farm-warning/10',
      value: stats.low, label: t('distributorHome.stats.lowStock.badge') },
    { icon: Store, iconClass: 'text-farm-success bg-farm-success/10',
      value: stats.shopkeepers, label: t('distributorHome.stats.shopkeepers') },
  ];

  return (
    <HomeShell>
      <HomeStakeholderStrip platform={platform} t={t} />
      <HomeSearchBar
        value={searchQ}
        onChange={setSearchQ}
        onSubmit={onSearchSubmit}
        testId="distributor-home-search-input"
      />
      <HomeBigButtons buttons={[
        { icon: ShoppingCart, label: t('distributorHome.productsMarketplace'), onClick: () => navigate('/marketplace'),
          testId: 'distributor-products-marketplace' },
        { icon: Wheat, label: t('distributorHome.produceMarketplace'), onClick: () => navigate('/produce'),
          testId: 'distributor-produce-marketplace' },
      ]} />
      <HomeLatestDeals deals={deals} />
      <HomeSectionTitle>{t('distributorHome.myStatistics')}</HomeSectionTitle>
      <HomeStatsCard tiles={statTiles} />
      <HomeTiles role="distributor" />
    </HomeShell>
  );
}
